/**
 * Pinned drand "quicknet" chain — League of Entropy mainnet, 3s unchained,
 * BLS12-381 G1 unchained scheme (RFC 9380). Stable since 2026.
 *
 * If LoE retires this chain, bump the hash here. Existing recorded proofs
 * reference the old hash via `RecordedSeed.drandChainHash` and stay verifiable
 * against the old chain's archived public key (queryable from any drand
 * mirror).
 *
 * Re-verify at deploy time:
 *
 *   curl -s https://api.drand.sh/chains | jq
 *   curl -s https://api.drand.sh/${chainHash}/info | jq
 *
 * Scheme: `bls-unchained-g1-rfc9380`.
 *   - Signatures live on G1 (48 bytes compressed = 96 hex chars).
 *   - Public key lives on G2 (96 bytes compressed = 192 hex chars).
 *   - Message before BLS = SHA-256(round_BE_8_byte).
 *   - randomness = SHA-256(signature_bytes).
 *
 * Public key is the canonical quicknet group pubkey published at
 * `https://api.drand.sh/${chainHash}/info`. Pinned here so the verifier never
 * trusts a network-fetched pubkey at audit time.
 */
export const DRAND_QUICKNET_CHAIN_HASH =
  "52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971";

export const DRAND_QUICKNET_PUBLIC_KEY =
  "83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a";

export const DRAND_QUICKNET_SCHEME = "bls-unchained-g1-rfc9380";

/** Round cadence in milliseconds — quicknet emits one round every 3 s. */
export const DRAND_QUICKNET_PERIOD_MS = 3_000;

/**
 * drand HTTP mirrors. Tried in order on each fetch until one responds; a
 * single-mirror outage degrades to the next without surfacing to the room.
 */
export const DRAND_MIRRORS: readonly string[] = [
  "https://api.drand.sh",
  "https://api2.drand.sh",
  "https://api3.drand.sh",
  "https://drand.cloudflare.com",
];

/** Human-readable display name for the verifier modal header. */
export const DRAND_DISPLAY_NAME = "drand (League of Entropy)";

/** Display name for the signer threshold. */
export const DRAND_SIGNER_LABEL = "14-org BLS threshold";

/**
 * chainHash → publicKey map for every chain whose proofs this verifier accepts.
 * The verifier looks up a recorded proof's `chainHash` here and verifies the
 * BLS signature against the matching pubkey. Add an entry when LoE rotates
 * chains so historical records keep verifying against the archived pubkey
 * (queryable via `https://api.drand.sh/${chainHash}/info` until a mirror prunes
 * it, which is why the pubkey is pinned in source rather than fetched).
 */
export const KNOWN_CHAINS: Readonly<Record<string, string>> = {
  [DRAND_QUICKNET_CHAIN_HASH]: DRAND_QUICKNET_PUBLIC_KEY,
};

/**
 * Look up the BLS public key for a given chain hash. Returns null when the
 * chain isn't in the registry — the verifier treats unknown chains as
 * `sig-invalid` since it has no way to verify their signatures.
 */
export function publicKeyForChain(chainHash: string): string | null {
  return KNOWN_CHAINS[chainHash.toLowerCase()] ?? null;
}
